import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isManager, ACTIVITIES } from "@/lib/constants";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Plus, Building2, Search } from "lucide-react";
import { toast } from "sonner";

export default function Firms() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [firms, setFirms] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", city: "", authorized_person: "", phone: "", email: "", activities: [] });

  const load = () => api.get("/firms").then(({ data }) => setFirms(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggleAct = (code) => setForm((f) => ({ ...f, activities: f.activities.includes(code) ? f.activities.filter((a) => a !== code) : [...f.activities, code] }));

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/firms", form);
      toast.success("Firma oluşturuldu.");
      setOpen(false);
      setForm({ title: "", city: "", authorized_person: "", phone: "", email: "", activities: [] });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const filtered = (firms || []).filter((f) => f.title.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-bold text-slate-900">Firmalar</h1>
        {isManager(user.role) && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-sky-600 hover:bg-sky-700" data-testid="add-firm-button"><Plus className="h-4 w-4 mr-2" /> Yeni Firma</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-display">Yeni Firma</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div className="space-y-1.5"><Label>Unvan *</Label><Input data-testid="firm-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Şehir</Label><Input data-testid="firm-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Yetkili</Label><Input data-testid="firm-authorized-input" value={form.authorized_person} onChange={(e) => setForm({ ...form, authorized_person: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>E-posta</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                </div>
                <div className="space-y-2">
                  <Label>ADR Faaliyetleri</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {ACTIVITIES.map((a) => (
                      <label key={a.code} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`firm-activity-${a.code}`}>
                        <Checkbox checked={form.activities.includes(a.code)} onCheckedChange={() => toggleAct(a.code)} /> {a.label}
                      </label>
                    ))}
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-sky-600 hover:bg-sky-700" disabled={saving} data-testid="firm-save-button">
                    {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Kaydet
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="relative max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input className="pl-9" placeholder="Firma ara..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="firm-search-input" />
      </div>

      {firms === null ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-sky-600" /></div>
      ) : filtered.length === 0 ? (
        <Card className="border-slate-200 border-dashed"><CardContent className="py-16 text-center text-slate-500">
          <Building2 className="h-10 w-10 mx-auto mb-3 text-slate-300" /> Firma bulunamadı.
        </CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((f) => (
            <Card key={f.id} className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/firmalar/${f.id}`)} data-testid={`firm-card-${f.id}`}>
              <CardContent className="pt-5">
                <div className="flex items-start justify-between">
                  <div className="h-10 w-10 rounded-lg bg-sky-50 flex items-center justify-center"><Building2 className="h-5 w-5 text-sky-600" /></div>
                  <StatusBadge tone={f.is_active ? "active" : "passive"}>{f.is_active ? "Aktif" : "Pasif"}</StatusBadge>
                </div>
                <div className="font-display font-semibold text-slate-900 mt-3">{f.title}</div>
                <div className="text-xs text-slate-500 mt-1">{f.city || "-"}</div>
                <div className="flex flex-wrap gap-1 mt-3">
                  {(f.activity_labels || []).slice(0, 4).map((a) => <span key={a} className="text-[11px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{a}</span>)}
                </div>
                <div className="text-xs text-slate-500 mt-3">Sorumlu: {f.responsible_tmgd_name || <span className="text-amber-600">Atanmadı</span>}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
